import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/Reveal";
import api from "@/lib/api";
import { startCheckout } from "@/lib/checkout";

function priceLabel(p) {
  if (p.checkout_enabled && p.price_amount) return `$${(p.price_amount / 100).toFixed(0)}`;
  return p.price_display || "Custom Quote";
}

export default function Pricing() {
  const [tab, setTab] = useState("professional");
  const [packages, setPackages] = useState([]);
  const [addonSel, setAddonSel] = useState({});

  const loadPackages = useCallback(() => {
    api.get("/packages").then((r) => setPackages(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadPackages(); }, [loadPackages]);

  const toggleAddon = (pkgId, key) =>
    setAddonSel((prev) => {
      const cur = prev[pkgId] || [];
      const next = cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key];
      return { ...prev, [pkgId]: next };
    });

  const items = packages.filter((p) => p.audience === tab && p.show_on_pricing !== false);
  const gridCols = tab === "business" ? "md:grid-cols-2 lg:grid-cols-4" : "md:grid-cols-2 lg:grid-cols-3";

  return (
    <div data-testid="pricing-page">
      <SEO title="Packages & Pricing" description="Boutique HR consulting and career services with clear, flexible packages for businesses and professionals." />

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 md:pt-24 pb-10 text-center">
        <Reveal>
          <span className="text-xs uppercase tracking-[0.25em] text-gold font-medium">Packages & Pricing</span>
          <h1 className="font-serif text-5xl md:text-6xl text-espresso mt-5">Clear, flexible, intentional.</h1>
          <p className="text-lg text-espresso/70 mt-5 max-w-2xl mx-auto">
            Choose the support that fits where you are. All services are delivered 100% virtually.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="inline-flex border border-gold/40 rounded-full p-1.5 mt-10">
            {[{ k: "professional", l: "For Professionals" }, { k: "business", l: "For Businesses" }].map((t) => (
              <button key={t.k} onClick={() => setTab(t.k)} data-testid={`pricing-tab-${t.k}`}
                className={`rounded-full px-7 py-3 text-sm font-medium transition-colors ${tab === t.k ? "bg-espresso text-ivory" : "text-espresso hover:text-gold"}`}>
                {t.l}
              </button>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <p className="text-sm text-espresso/60 mt-6">
            Not sure which fits?{" "}
            <Link to="/book" data-testid="pricing-intro-call" className="text-gold font-medium underline underline-offset-4 hover:text-espresso transition-colors">
              Book a free 15-min intro call
            </Link>
          </p>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className={`grid gap-8 ${gridCols}`}>
          {items.map((p, i) => {
            const sel = addonSel[p.id] || [];
            const addons = p.addons || [];
            const addonTotal = addons.filter((a) => sel.includes(a.key)).reduce((s, a) => s + a.amount, 0);
            const total = (p.price_amount || 0) + addonTotal;
            return (
            <Reveal key={p.id} delay={(i % 4) * 0.08}>
              <div data-testid={`pricing-card-${p.id}`} className={`rounded-2xl p-8 h-full flex flex-col border ${p.featured ? "border-gold" : "border-champagne/25"}`}>
                <div className="flex flex-col flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs uppercase tracking-[0.2em] text-champagne">{p.category}</span>
                    {p.featured && <span className="text-xs border border-gold text-gold rounded-full px-3 py-1 whitespace-nowrap">{p.badge || "Featured"}</span>}
                  </div>
                  <h3 className="font-serif text-2xl mt-3 text-espresso">{p.name}</h3>
                  <div className="mt-4">
                    <span className="font-serif text-4xl text-espresso">{priceLabel(p)}</span>
                  </div>
                  <p className="mt-4 text-sm leading-relaxed text-espresso/70">{p.description}</p>
                  <ul className="mt-5 space-y-2 flex-1">
                    {(p.inclusions || []).map((inc) => (
                      <li key={inc} className="flex gap-2 text-sm text-espresso/75">
                        <Check size={16} className="text-gold mt-0.5 shrink-0" />{inc}
                      </li>
                    ))}
                  </ul>
                  {p.footnote && (
                    <p className="mt-4 text-xs italic text-espresso/55 leading-relaxed">{p.footnote}</p>
                  )}
                  {addons.length > 0 && (
                    <div className="mt-5 border-t border-champagne/20 pt-4 space-y-2.5">
                      <p className="text-[11px] uppercase tracking-[0.2em] text-gold">Optional add-ons</p>
                      {addons.map((a) => (
                        <label key={a.key} className="flex items-center justify-between gap-3 cursor-pointer text-sm text-espresso/80 hover:text-espresso transition-colors">
                          <span className="flex items-center gap-2.5">
                            <input type="checkbox" checked={sel.includes(a.key)} onChange={() => toggleAddon(p.id, a.key)}
                              data-testid={`addon-${p.id}-${a.key}`} className="w-4 h-4 accent-espresso rounded" />
                            {a.name}
                          </span>
                          <span className="text-espresso/60 whitespace-nowrap">+${(a.amount / 100).toFixed(0)}</span>
                        </label>
                      ))}
                    </div>
                  )}
                  {addonTotal > 0 && (
                    <p className="mt-4 text-sm text-espresso" data-testid={`pricing-total-${p.id}`}>
                      Total: <span className="font-serif text-xl text-espresso">${(total / 100).toFixed(0)}</span>
                    </p>
                  )}
                  {p.checkout_enabled && p.price_amount ? (
                    <button onClick={() => startCheckout(p.id, "", sel)} data-testid={`pricing-buy-${p.id}`}
                      className="mt-6 rounded-full px-6 py-3 font-medium transition-colors bg-espresso text-ivory hover:bg-forest">
                      Purchase
                    </button>
                  ) : (
                    <Link to={p.cta_link || (p.audience === "business" ? "/for-businesses#inquiry" : "/book")} data-testid={`pricing-inquire-${p.id}`}
                      className="mt-6 text-center rounded-full px-6 py-3 font-medium border border-espresso text-espresso hover:bg-espresso hover:text-ivory transition-colors">
                      {p.cta_label || (p.audience === "business" ? "Request a Proposal" : "Book a Session")}
                    </Link>
                  )}
                </div>
              </div>
            </Reveal>
            );
          })}
        </div>
        {tab === "business" && (
          <Reveal>
            <p className="text-center text-sm text-espresso/60 mt-12 max-w-3xl mx-auto leading-relaxed">
              All services are delivered virtually. Project scope, timelines, and pricing are confirmed before work begins. Custom proposals are available.
            </p>
          </Reveal>
        )}
      </section>
    </div>
  );
}
